// GUNDI-5639 / GUNDI-5642: which UI tree renders, chosen per browser TAB.
//
// "legacy" (default/absent) is the frozen pre-flow-map portal under
// src/legacy/; "new" is the current portal, reachable only through the hidden
// unlock in the legacy nav (NewVersionUnlock). The routing IS the switch — no
// boot guards, no cross-page redirects — so the value is read at render time
// and subscribers re-render when it flips. Removed at sunset (Phase 5)
// together with src/legacy/.
export type UiVersion = "new" | "legacy";

export const UI_VERSION_KEY = "gundi_ui_version";

// sessionStorage, not localStorage: the choice must not outlive the tab
// (GUNDI-5642 — back to legacy on logout, on closing the tab, and in a newly
// opened tab). Per tab is also the least code: nothing to synchronise.
//
// One caveat, by design: browsers COPY a tab's session storage into a tab
// duplicated or opened from it (Duplicate tab, ctrl/cmd-click on an in-app
// link), so such a tab inherits "new". A tab opened from scratch, and every
// other browser, still starts on legacy. Distinguishing a copied namespace
// would mean treating a fresh navigation as a reset, which would also drop the
// choice on any full-page return to the portal — a worse trade.
//
// Last value set in this page view, used whenever storage cannot be trusted to
// answer for it — storage disabled, private mode, or a quota-full store whose
// reads still work while writes throw. Without this a switch in any of those
// cases would be a no-op: the write fails, and the read that follows reports
// whatever stale/absent value the store holds.
let inMemoryVersion: UiVersion | null = null;
let persisted = true;

export function getUiVersion(): UiVersion {
    // A failed write means the store no longer reflects the user's choice, even
    // if reads keep succeeding.
    if (!persisted && inMemoryVersion) return inMemoryVersion;
    try {
        return sessionStorage.getItem(UI_VERSION_KEY) === "new" ? "new" : "legacy";
    } catch {
        return inMemoryVersion ?? "legacy";
    }
}

const listeners = new Set<() => void>();

export function setUiVersion(version: UiVersion) {
    inMemoryVersion = version;
    try {
        if (version === "new") sessionStorage.setItem(UI_VERSION_KEY, "new");
        // "legacy" is the default, so it is stored as the ABSENCE of the key —
        // one less orphan to clean up at sunset.
        else sessionStorage.removeItem(UI_VERSION_KEY);
        persisted = true;
    } catch {
        // Not persisted (the choice lasts this page view only), but the notify
        // below still swaps the tree so the click is never a no-op.
        persisted = false;
    }
    listeners.forEach((l) => l());
}

// Called on logout by both Navs. Keycloak's logout redirect stays in the same
// tab, so without this the next person to log in on this machine would land on
// the new version they never unlocked.
//
// Deliberately does NOT notify subscribers: the page is on its way out, and
// swapping the tree here would mount the whole legacy portal — navbar, EULA and
// user-details requests — for the instant before the redirect lands.
export function clearUiVersion() {
    inMemoryVersion = null;
    persisted = true;
    try {
        sessionStorage.removeItem(UI_VERSION_KEY);
    } catch {
        // No storage: the in-memory reset above is the whole job.
    }
}

export function subscribeUiVersion(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
